import { useMapDispatch, useMapState } from "@comact/crc";
import React from "react";
import { IMillNode, INode } from "./model";
import { IPerspective } from "./perspectives/model";
import * as requests from "./requests";
import { getBasicNodeById, getDetailedNodeById } from "./selectors";

interface IPageNodeContext<T extends INode> {
    nodeId: string;
    node: T;
    locationNodeId: string;
    millNode: IMillNode;
    millNodeId: string;
    perspective: IPerspective;
    setPageName: (overridePageName: string) => void;
}

export const PageNodeContext = React.createContext<IPageNodeContext<INode>>({
    nodeId: null,
    node: null,
    locationNodeId: null,
    millNode: null,
    millNodeId: null,
    perspective: null,
    setPageName: null,
});

export const usePageNodeContext = <T extends INode>() => React.useContext<IPageNodeContext<T>>(PageNodeContext as React.Context<IPageNodeContext<T>>);

export const useGetOrFetchNode = <T extends INode>(nodeId: string) => {
    const node = useMapState((state) => {
        if (!nodeId) return null;

        const candidate = getBasicNodeById(state, nodeId);
        const detailedCandidate = getDetailedNodeById<T>(state, nodeId);

        return detailedCandidate && candidate?.modificationDate <= detailedCandidate.modificationDate ? detailedCandidate : null;
    }, [nodeId]);

    const fetchOneNode = useMapDispatch((dispatch) => (id: string) => dispatch(requests.fetchOneNode(id)));

    React.useEffect(() => {
        if (nodeId && !node) fetchOneNode(nodeId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodeId, node]);

    return node;
};
